import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { BUSINESS, WA_MESSAGES } from "@/config/business";
import { WhatsAppButton } from "./CtaButtons";

const NAV_LINKS = [
  { label: "Home", href: "#home", testId: "nav-home" },
  { label: "Services", href: "#services", testId: "nav-services" },
  { label: "About", href: "#about", testId: "nav-about" },
  { label: "Location", href: "#location", testId: "nav-location" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 backdrop-blur-xl transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled || open
          ? "border-b border-[#E7E2DA] bg-[#FAF8F5]/95 shadow-[0_4px_20px_-2px_rgba(44,39,32,0.08)]"
          : "border-b border-transparent bg-[#FAF8F5]/80"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-[72px] sm:px-8">
        <a
          href="#home"
          data-testid="header-logo"
          className="flex items-center gap-3 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]"
          aria-label="GK Wellness & Luxury Spa — back to top"
        >
          <img
            src={BUSINESS.logo}
            alt="GK Wellness & Luxury Spa logo"
            className="h-10 w-10 rounded-full object-cover ring-1 ring-[#C5A059]/40 sm:h-11 sm:w-11"
            width="44"
            height="44"
          />
          <span className="leading-tight">
            <span className="block font-serif text-lg font-semibold tracking-tight text-[#1C1917] sm:text-xl">
              GK Wellness
            </span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9E7B36] sm:text-[11px]">
              &amp; Luxury Spa
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={link.testId}
              className="rounded-sm text-sm font-medium text-[#57534E] transition-colors hover:text-[#1C1917] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={BUSINESS.tel}
            data-testid="header-phone-cta"
            aria-label={`Call GK Wellness on ${BUSINESS.phoneIntl}`}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-3 text-sm font-semibold text-[#1C1917] transition-colors hover:text-[#9E7B36] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {BUSINESS.phoneDisplay}
          </a>
          <WhatsAppButton
            message={WA_MESSAGES.general}
            label="Book Appointment"
            testId="header-whatsapp-cta"
            size="sm"
          />
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[#1C1917] transition-colors hover:bg-[#F4EFEA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] lg:hidden"
        >
          {open ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-[#E7E2DA] bg-[#FAF8F5] px-4 pb-6 pt-2 lg:hidden"
        >
          <nav className="flex flex-col" aria-label="Mobile">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-${link.testId}`}
                onClick={() => setOpen(false)}
                className="border-b border-[#E7E2DA]/70 py-3.5 text-base font-medium text-[#1C1917] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="mt-4 flex flex-col gap-3">
            <WhatsAppButton
              message={WA_MESSAGES.general}
              label="Book on WhatsApp"
              testId="mobile-menu-whatsapp-cta"
              className="w-full"
            />
            <a
              href={BUSINESS.tel}
              data-testid="mobile-menu-call-cta"
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-[#1C1917]/25 px-6 py-3 text-sm font-semibold text-[#1C1917]"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              Call {BUSINESS.phoneDisplay}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
